/**
 * formatPages.js — a format's pages, as page types the engine can draft.
 *
 * `formats.js` states each template's pages as data; this turns one into
 * something `draftPages` can build:
 *
 *   use: '<recipe id>'   the recipe itself, under the template page's key, with
 *                        any stated overrides (the views of a comparison page,
 *                        its captions). 'set/id' names another set's recipe.
 *   use: 'g-…'           a generic page carrying the template's caption presets,
 *                        its header title and its view.
 *   hand: '<why>'        listed and never drafted — the page is made in GIMP.
 *   omit: '<why>'        not a page of the report at all. Still in formats.js so
 *                        the template's own page list stays complete and its
 *                        `v:` citation is not lost, but never offered, never
 *                        drafted, and not counted in the report's page total.
 *
 * Kept out of formats.js so the data file stays data, and out of pageTypes.js so
 * draft.js can use it without importing the page-list layer. It takes the recipe
 * sets as an argument rather than importing draft.js, which would be a cycle.
 */
import { formatFor } from './formats.js'
import { genericPage } from './genericPages.js'

/** Captions a generic page prints unless the operator types over them. */
const captionsOf = (p) => ({ tc: p.tc ?? '', bc: p.bc ?? '' })

function pageTypeFor(format, p, recipesByKey) {
  // `listTitle` is what the page list calls this template page. A recipe keeps
  // its OWN `title`, which its notes and its drawn heading use — renaming those
  // would change the document.
  const common = {
    id: p.key,
    listTitle: p.title,
    templatePage: p.v,          // the layer group in the template — the citation
    formatId: format.id,
  }
  // A page the Builder cannot finish. `block: null`, so it is never drafted into
  // the report — the team makes it and merges it in.
  //
  // `sheet` is how that page is laid out when the team DOWNLOADS it to work on:
  // the chrome, the heading and its renders, with the drawing area left empty.
  // Taken from the page's own template variant, not chosen here — the four
  // orthognathic pages are `grid` in the XCF and the eleven others are
  // `single-view`. A page with no `sheet` downloads as chrome only, which is
  // right for the text pages (the helmet leaflet, the QR page).
  if (p.hand) {
    return {
      ...common,
      title: p.title,
      hand: p.hand,
      block: null,
      repeatable: false,
      ...(p.sheet ? { sheet: p.sheet } : {}),
    }
  }

  if (p.use.startsWith('g-')) {
    const generic = genericPage(p.use)
    if (!generic) return null
    return {
      ...generic,
      ...common,
      // A generic page takes the template page's name, so a note about it says
      // which page of the template it is.
      title: p.title,
      genericId: p.use,
      captions: captionsOf(p),
      ...(p.header ? { headerTitle: p.header } : {}),
      ...(p.view ? { view: p.view } : {}),
      // The views a row page draws, when the template states them — `g-view-row`
      // reads this. Without it the row fell back to four and the three-view leg
      // pages drew a fourth cell the template does not have.
      ...(p.views ? { views: p.views } : {}),
    }
  }

  const [setKey, recipeId] = p.use.includes('/') ? p.use.split('/') : [format.recipes, p.use]
  const recipe = (recipesByKey[setKey] ?? []).find((r) => r.id === recipeId)
  if (!recipe) return null
  return {
    ...recipe,
    ...common,
    recipeId,
    recipeSet: setKey,
    ...(p.views ? { views: p.views } : {}),
    ...(p.header ? { headerTitle: p.header } : {}),
    ...(p.leftCaption != null ? { leftCaption: p.leftCaption } : {}),
    ...(p.rightCaption != null ? { rightCaption: p.rightCaption } : {}),
    ...(p.bottomCaption != null ? { bottomCaption: p.bottomCaption } : {}),
    ...(p.tc != null ? { topCaption: p.tc } : {}),
  }
}

/**
 * Every page of a format's template, in the template's order.
 *
 * @param {string} formatId
 * @param {object} recipesByKey  RECIPES from draft.js
 */
export function formatPageTypes(formatId, recipesByKey = {}) {
  const format = formatFor(formatId)
  if (!format) return []
  return format.pages
    // `omit` pages are dropped HERE, at the one place a format becomes a page
    // list, so nothing downstream can offer one, draft one, or count one into
    // "of N" in the footer. The orthognathic agreement and its two terms pages
    // are enclosed by the team; the template's own page-number layer already
    // reads "Page 09 of 14", which is its 17 pages less these three.
    .filter((p) => !p.omit)
    .map((p) => pageTypeFor(format, p, recipesByKey))
    .filter(Boolean)
}

/** Template pages this format deliberately leaves out of the report. */
export const omittedPages = (formatId) =>
  (formatFor(formatId)?.pages ?? []).filter((p) => p.omit)

/** The pages of a format that the Builder can actually draft. */
export const buildablePages = (formatId, recipesByKey) =>
  formatPageTypes(formatId, recipesByKey).filter((t) => !t.hand)
